import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SectionHeading } from "./Reveal";
import { useLanguage } from "@/context/LanguageContext";

const Story = () => {
  const ref = useRef(null);
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 65%", "end 75%"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const timelineItems = [
    { year: t("story.t1Year"), title: t("story.t1Title"), desc: t("story.t1Desc") },
    { year: t("story.t2Year"), title: t("story.t2Title"), desc: t("story.t2Desc") },
    { year: t("story.t3Year"), title: t("story.t3Title"), desc: t("story.t3Desc") },
    { year: t("story.t4Year"), title: t("story.t4Title"), desc: t("story.t4Desc") },
  ];

  return (
    <section id="story" className="relative py-24 md:py-36 bg-white overflow-hidden" data-testid="story-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading
          eyebrow={t("story.eyebrow")}
          title={`${t("story.title")} ${t("story.titleHighlight")}`}
          subtitle={t("story.subtitle")}
          testid="story"
        />

        <div ref={ref} className="relative mt-20 max-w-4xl mx-auto">
          {/* Track */}
          <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px bg-[#40916c]/12 md:-translate-x-1/2" />
          {/* Progress fill */}
          <motion.div
            style={{ height: lineHeight }}
            className="absolute left-[19px] md:left-1/2 top-0 w-px bg-[#40916c] md:-translate-x-1/2 origin-top"
          />

          <div className="space-y-14 md:space-y-24">
            {timelineItems.map((item, i) => {
              const leftSide = i % 2 === 0;
              return (
                <div
                  key={i}
                  className={`relative md:grid md:grid-cols-2 md:gap-16 items-center ${leftSide ? "" : "md:[direction:rtl]"}`}
                  data-testid={`timeline-item-${i}`}
                >
                  {/* Node */}
                  <span className="absolute left-[13px] md:left-1/2 top-1.5 md:top-1/2 md:-translate-y-1/2 md:-translate-x-1/2 z-10 h-3.5 w-3.5 rounded-full bg-[#40916c] ring-4 ring-white shadow" />

                  <motion.div
                    initial={{ opacity: 0, x: leftSide ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className={`[direction:ltr] pl-12 md:pl-0 ${leftSide ? "md:text-right md:pr-4" : "md:pl-4"}`}
                  >
                    <span className="font-display text-4xl md:text-5xl font-bold text-[#40916c]/25 block mb-1">
                      {item.year}
                    </span>
                    <h3 className="font-display text-xl md:text-2xl font-semibold text-[#2d6a4f]">
                      {item.title}
                    </h3>
                    <p className="font-body text-[15px] text-[#5b6b62] leading-relaxed mt-2 md:max-w-sm md:inline-block">
                      {item.desc}
                    </p>
                  </motion.div>

                  <div className="hidden md:block" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Story;
